import { Avatar, Card, CardContent, Box, Typography, Chip, Skeleton, Button } from "@mui/material";
import { useTheme } from "../../../contexts/ThemeContext";
import { Info as InfoIcon } from '@mui/icons-material';

export const MetricCard = ({ title, value, icon, color, subtitle, status, loading, actionButton, onClick }) => {
	const { theme } = useTheme();

	const isLoading = loading || value === undefined;

	if (isLoading) {
		return (
			<Card sx={{
				height: '100%',
				background: theme.background.tertiary,
				border: `1px solid ${theme.border.medium}`,
				borderRadius: '16px',
				position: 'relative',
				overflow: 'hidden',
			}}>
				<CardContent sx={{ p: 3, position: 'relative' }}>
					<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
						<Skeleton
							variant="circular"
							width={56}
							height={56}
							sx={{ bgcolor: theme.background.secondary }}
						/>
						<Skeleton
							variant="rectangular"
							width={60}
							height={24}
							sx={{
								borderRadius: '12px',
								bgcolor: theme.background.secondary
							}}
						/>
					</Box>

					<Skeleton
						variant="text"
						width="60%"
						height={48}
						sx={{
							mb: 1,
							bgcolor: theme.background.secondary
						}}
					/>

					<Skeleton
						variant="text"
						width="40%"
						height={28}
						sx={{
							mb: 1,
							bgcolor: theme.background.secondary
						}}
					/>

					<Skeleton
						variant="text"
						width="80%"
						height={20}
						sx={{ bgcolor: theme.background.secondary }}
					/>
				</CardContent>
			</Card>
		);
	}

	return (
		<Card sx={{
			height: '100%',
			background: theme.background.tertiary,
			border: `1px solid ${theme.border.medium}`,
			borderRadius: '16px',
			transition: 'all 0.3s ease',
			position: 'relative',
			overflow: 'hidden',
			cursor: onClick ? "pointer" : "default",
			'&:hover': {
				transform: 'translateY(-2px)',
				boxShadow: theme.isDarkMode
					? `0 8px 32px rgba(74, 159, 234, 0.15)`
					: `0 8px 32px rgba(0,0,0,0.12)`,
				borderColor: color,
			},
			'&::before': {
				content: '""',
				position: 'absolute',
				top: 0,
				left: 0,
				right: 0,
				height: '4px',
				background: `linear-gradient(90deg, ${color}, ${color}80)`,
			}
		}}
			onClick={(e) => onClick && onClick(e)}
		>
			<CardContent sx={{ p: 3, position: 'relative' }}>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
					<Avatar sx={{
						backgroundColor: `${color}15`,
						color: color,
						width: 56,
						height: 56,
					}}>
						{icon}
					</Avatar>
					{status && (
						<Chip
							label={status}
							size="small"
							sx={{
								backgroundColor: `${color}20`,
								color: color,
								fontWeight: 'bold',
								fontSize: '0.75rem',
								fontFamily: theme.fontFamily
							}}
						/>
					)}
				</Box>

				<Typography variant="h3" sx={{
					fontWeight: 'bold',
					color: theme.text.primary,
					fontFamily: theme.fontFamily,
					mb: 1,
					lineHeight: 1.2
				}}>
					{value !== undefined && value < 0 ? "N/A" : typeof value === 'number' ? value.toLocaleString() : value}
				</Typography>

				<Box sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between"
				}}>
					<Typography variant="h6" sx={{
						color: theme.text.secondary,
						fontFamily: theme.fontFamily,
						fontWeight: 'medium',
						mb: subtitle ? 1 : 0
					}}>
						{title}
					</Typography>
					{actionButton && !actionButton?.hide && <>
						<Button
							disabled={actionButton.disabled}
							variant={actionButton.variant ?? "outlined"}
							onClick={actionButton.onClick}
							sx={{
								fontFamily: theme.fontFamily,
								mb: subtitle ? 1 : 0,
								...(actionButton.sx ?? {})
							}}
						>
							{actionButton.title}
						</Button>
					</>}
				</Box>
				{subtitle && (
					<Typography variant="body2" sx={{
						color: theme.text.tertiary,
						fontFamily: theme.fontFamily,
						display: 'flex',
						alignItems: 'center',
						gap: 0.5
					}}>
						<InfoIcon fontSize="small" />
						{subtitle}
					</Typography>
				)}
			</CardContent>
		</Card>
	);
};